import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiEdit2, FiTrash2, FiX, FiCheck, FiPlus,
  FiFolder, FiFolderPlus, FiChevronDown, FiChevronRight, FiCornerUpRight, FiLayers, FiUsers,
} from 'react-icons/fi';
import { leftGroupText } from './GroupChat';
import { LuCodeXml } from 'react-icons/lu';
import { IoFileTrayFullOutline } from 'react-icons/io5';
import {
  listConversations,
  updateConversationName,
  deleteConversations,
  createFolder,
  listFolders,
  renameFolder,
  deleteFolder,
  setConversationFolder,
} from '../utils/conversationApi';
import FocusModeTag from './FocusModeTag';
import { focusModeTagMatches } from '../utils/focusModes';
import './ConversationsSidebar.css';

const FOLDERS_SECTION_COLLAPSED_STORAGE_KEY = 'kai_folders_section_collapsed';

// A folder holding conversations saved out of a project (one the user left,
// or deleted) gets the Projects icon (the same one as the header's Projects
// button), so it reads as "from a project", not as one of their own folders.
const FolderIcon = ({ folder, size, className }) => (
  folder.from_project ? (
    <FiLayers size={size} className={className} title="Saved from a project" />
  ) : (
    <FiFolder size={size} className={className} />
  )
);

const ConversationsSidebar = ({
  selectedModel,
  activeConversationId,
  onConversationSelect,
  onNewConversation,
  onClose,
  refreshTrigger,
  isOpen,
  loadingConversationId,
}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [filterText, setFilterText] = useState('');
  const [showOnlyProjects, setShowOnlyProjects] = useState(false);
  const [organizeMode, setOrganizeMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteModalIds, setDeleteModalIds] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Folders
  const [folders, setFolders] = useState([]);
  // Until the first load answers, "no folders" isn't known yet.
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [folderDeleteId, setFolderDeleteId] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  // Which individual folders are expanded -- session-only (not persisted),
  // and empty by default, so every folder starts CLOSED on a fresh page
  // load/refresh no matter what was expanded before. The whole-"Folders"-
  // section collapse below is a separate, deliberately-persisted preference
  // (see foldersSectionCollapsed) -- this is specifically about not
  // remembering which individual folders were left open.
  const [expandedFolderIds, setExpandedFolderIds] = useState(() => new Set());
  // Folders the user closed while searching. A search opens every folder
  // holding a match, but a click can still close one; emptied when the
  // search box is cleared, so the next search shows every match again.
  const [searchClosedFolderIds, setSearchClosedFolderIds] = useState(() => new Set());
  // Whether the whole "Folders" section is collapsed (hides every folder
  // group at once); distinct from collapsing one folder at a time above.
  const [foldersSectionCollapsed, setFoldersSectionCollapsed] = useState(() => {
    try {
      return localStorage.getItem(FOLDERS_SECTION_COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [draggedConversationId, setDraggedConversationId] = useState(null);
  // A folder_id, or 'unfiled'.
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [movingSelection, setMovingSelection] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [moveMenuConversationId, setMoveMenuConversationId] = useState(null);
  // The per-conversation menu and the organize-mode batch menu are mutually
  // exclusive (the per-conversation button only renders outside organize
  // mode), so one shared search box is safe.
  const [folderSearchText, setFolderSearchText] = useState('');
  // Conversation ids with a folder move in flight (drag-and-drop or the
  // per-row "Move to folder" menu; batch move already skips polling via
  // organizeMode). A ref, not state, since it only needs to gate the poll -
  // it doesn't drive a render.
  const movingConversationIdsRef = useRef(new Set());

  // Close any open "move to folder" menu on an outside click, since both the
  // per-conversation menu and the organize-mode batch menu are lightweight
  // dropdowns without a modal backdrop.
  useEffect(() => {
    if (!moveMenuConversationId && !showMoveMenu) return;

    const handleOutsideClick = (e) => {
      // Guard the target type: closest() only exists on Element, and a throw
      // in here would leave the menu stuck open with no way to dismiss it.
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.conversation-move-wrapper') || target.closest('.move-to-folder-wrapper')) {
        return;
      }
      setMoveMenuConversationId(null);
      setShowMoveMenu(false);
      setFolderSearchText('');
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [moveMenuConversationId, showMoveMenu]);

  const getFolderMatches = (excludeFolderId) => {
    const base = folders.filter((f) => f.folder_id !== excludeFolderId);
    if (!folderSearchText.trim()) return base;
    const q = folderSearchText.toLowerCase();
    return base.filter((f) => f.name.toLowerCase().includes(q));
  };

  const fetchConversations = useCallback(async () => {
    if (editingId || organizeMode || movingConversationIdsRef.current.size > 0) return;

    try {
      const data = await listConversations();
      // A move could have started while this request was in flight - applying
      // a response fetched before it would clobber the optimistic move and
      // leave the conversation looking like it's back in its old group until
      // the next poll.
      if (movingConversationIdsRef.current.size > 0) return;
      setConversations(data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [editingId, organizeMode]);

  const fetchFolders = useCallback(async () => {
    if (editingFolderId) return;

    try {
      const data = await listFolders();
      setFolders(data);
    } catch (error) {
      console.error('Error fetching folders:', error);
    } finally {
      setFoldersLoaded(true);
    }
  }, [editingFolderId]);

  // Only poll when sidebar is open
  useEffect(() => {
    if (!isOpen) return;

    fetchConversations();
    fetchFolders();
    const interval = setInterval(() => {
      fetchConversations();
      fetchFolders();
    }, 5000);
    return () => clearInterval(interval);
  }, [isOpen, fetchConversations, fetchFolders]);

  // Refresh when refreshTrigger changes (for immediate updates). It's bumped
  // after every reply, so this fetches once per bump: not again when the
  // fetchers change, and not while closed (opening fetches anyway).
  const refreshTriggerSeenRef = useRef(refreshTrigger);
  useEffect(() => {
    if (refreshTrigger === refreshTriggerSeenRef.current) return;
    refreshTriggerSeenRef.current = refreshTrigger;
    if (!isOpen) return;
    fetchConversations();
    fetchFolders();
  }, [refreshTrigger, isOpen, fetchConversations, fetchFolders]);

  useEffect(() => {
    try {
      localStorage.setItem(FOLDERS_SECTION_COLLAPSED_STORAGE_KEY, String(foldersSectionCollapsed));
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }, [foldersSectionCollapsed]);

  useEffect(() => {
    if (!filterText.trim()) {
      setSearchClosedFolderIds((prev) => (prev.size ? new Set() : prev));
    }
  }, [filterText]);

  // Reset organize mode when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      setOrganizeMode(false);
      setSelectedIds(new Set());
      setDeleteModalIds(null);
      setIsCreatingFolder(false);
      setNewFolderName('');
      setEditingFolderId(null);
      setEditingFolderName('');
      setFolderDeleteId(null);
      setShowMoveMenu(false);
      setMoveMenuConversationId(null);
      setFolderSearchText('');
    }
  }, [isOpen]);

  const handleStartEdit = (conversation) => {
    setEditingId(conversation.conversation_id);
    setEditingName(conversation.name);
  };

  const handleSaveEdit = async (conversationId) => {
    if (!editingName.trim()) return;

    try {
      await updateConversationName(conversationId, editingName);
      setConversations(conversations.map(c =>
        c.conversation_id === conversationId
          ? { ...c, name: editingName }
          : c
      ));
      setEditingId(null);
      setEditingName('');
    } catch (error) {
      console.error('Error updating conversation:', error);
      alert('Failed to update conversation name');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const openDeleteModal = (ids) => {
    setDeleteModalIds(ids);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalIds(null);
  };

  const confirmDelete = async () => {
    if (!deleteModalIds || deleteModalIds.length === 0) return;

    const idsToDelete = deleteModalIds;
    setDeleting(true);

    try {
      await deleteConversations(idsToDelete);
      const deletedSet = new Set(idsToDelete);
      setConversations((prev) => prev.filter((c) => !deletedSet.has(c.conversation_id)));

      if (idsToDelete.includes(activeConversationId) && onConversationSelect) {
        onConversationSelect(null);
      }

      setSelectedIds(new Set());
      setOrganizeMode(false);
      setDeleteModalIds(null);
    } catch (error) {
      console.error('Error deleting conversation(s):', error);
      alert(error.message || 'Failed to delete conversation(s)');
      fetchConversations();
    } finally {
      setDeleting(false);
    }
  };

  const enterOrganizeMode = () => {
    setOrganizeMode(true);
    setSelectedIds(new Set());
    setMoveMenuConversationId(null);
  };

  const cancelOrganizeMode = () => {
    setOrganizeMode(false);
    setSelectedIds(new Set());
    setShowMoveMenu(false);
    setFolderSearchText('');
    setMoveMenuConversationId(null);
  };

  const toggleSelection = (conversationId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });
  };

  const handleOpenBatchDeleteModal = () => {
    if (selectedIds.size === 0) return;
    openDeleteModal([...selectedIds]);
  };

  const handleLoadConversation = (conversation) => {
    if (loadingConversationId) return;
    if (onConversationSelect) {
      onConversationSelect(conversation);
    }
  };

  // ---- Folders ----

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;

    try {
      const folder = await createFolder(name);
      setFolders((prev) => [...prev, folder]);
      setNewFolderName('');
      setIsCreatingFolder(false);
      // Show the new folder, even under a heading collapsed before.
      setFoldersSectionCollapsed(false);
    } catch (error) {
      console.error('Error creating folder:', error);
      alert(error.message || 'Failed to create folder');
    }
  };

  const handleCancelCreateFolder = () => {
    setIsCreatingFolder(false);
    setNewFolderName('');
  };

  const handleStartEditFolder = (folder) => {
    setEditingFolderId(folder.folder_id);
    setEditingFolderName(folder.name);
  };

  const handleCancelFolderEdit = () => {
    setEditingFolderId(null);
    setEditingFolderName('');
  };

  const handleSaveFolderEdit = async (folderId) => {
    if (!editingFolderName.trim()) return;

    try {
      await renameFolder(folderId, editingFolderName);
      setFolders((prev) => prev.map((f) =>
        f.folder_id === folderId ? { ...f, name: editingFolderName } : f
      ));
      setEditingFolderId(null);
      setEditingFolderName('');
    } catch (error) {
      console.error('Error renaming folder:', error);
      alert(error.message || 'Failed to rename folder');
    }
  };

  const openFolderDeleteModal = (folderId) => setFolderDeleteId(folderId);

  const closeFolderDeleteModal = () => {
    if (deletingFolder) return;
    setFolderDeleteId(null);
  };

  const confirmDeleteFolder = async () => {
    if (!folderDeleteId) return;
    setDeletingFolder(true);

    try {
      await deleteFolder(folderDeleteId);
      setFolders((prev) => prev.filter((f) => f.folder_id !== folderDeleteId));
      setConversations((prev) => prev.map((c) =>
        c.folder_id === folderDeleteId ? { ...c, folder_id: null } : c
      ));
      // Drop the deleted folder's expanded entry too -- session-only now
      // (no longer persisted), but still worth pruning so a stale id can't
      // linger in this set for the rest of the session.
      setExpandedFolderIds((prev) => {
        if (!prev.has(folderDeleteId)) return prev;
        const next = new Set(prev);
        next.delete(folderDeleteId);
        return next;
      });
      setFolderDeleteId(null);
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert(error.message || 'Failed to delete folder');
    } finally {
      setDeletingFolder(false);
    }
  };

  const toggleFoldersSection = () => setFoldersSectionCollapsed((prev) => !prev);

  const toggleFolderCollapsed = (folderId, openForSearch) => {
    (openForSearch ? setSearchClosedFolderIds : setExpandedFolderIds)((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  // ---- Drag-and-drop: file a conversation into a folder, or back to Unfiled ----

  const handleConversationDragStart = (e, conversationId) => {
    setDraggedConversationId(conversationId);
    e.dataTransfer.setData('text/plain', conversationId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleConversationDragEnd = () => {
    setDraggedConversationId(null);
    setDragOverTarget(null);
  };

  const handleFolderDragOver = (e, target) => {
    if (!draggedConversationId) return;
    e.preventDefault();
    setDragOverTarget(target);
  };

  const handleFolderDragLeave = (target) => {
    setDragOverTarget((prev) => (prev === target ? null : prev));
  };

  // Shared by drag-drop, the per-conversation "Move to folder" menu, and the
  // organize-mode batch move action. folderId is null to move to Unfiled.
  const moveConversationToFolder = async (conversationId, folderId) => {
    const conversation = conversations.find((c) => c.conversation_id === conversationId);
    if (!conversation || (conversation.folder_id || null) === folderId) return;

    const previousFolderId = conversation.folder_id || null;
    setConversations((prev) => prev.map((c) =>
      c.conversation_id === conversationId ? { ...c, folder_id: folderId } : c
    ));
    movingConversationIdsRef.current.add(conversationId);

    try {
      await setConversationFolder(conversationId, folderId);
    } catch (error) {
      console.error('Error moving conversation:', error);
      alert(error.message || 'Failed to move conversation');
      setConversations((prev) => prev.map((c) =>
        c.conversation_id === conversationId ? { ...c, folder_id: previousFolderId } : c
      ));
    } finally {
      movingConversationIdsRef.current.delete(conversationId);
    }
  };

  const handleFolderDrop = async (e, target) => {
    e.preventDefault();
    const conversationId = draggedConversationId || e.dataTransfer.getData('text/plain');
    setDraggedConversationId(null);
    setDragOverTarget(null);
    if (!conversationId) return;

    await moveConversationToFolder(conversationId, target === 'unfiled' ? null : target);
  };

  const toggleMoveMenuFor = (conversationId) => {
    setMoveMenuConversationId((prev) => (prev === conversationId ? null : conversationId));
    setFolderSearchText('');
  };

  const handleMoveConversationTo = async (conversationId, folderId) => {
    setMoveMenuConversationId(null);
    setFolderSearchText('');
    await moveConversationToFolder(conversationId, folderId);
  };

  const handleMoveSelectedToFolder = async (folderId) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    setMovingSelection(true);
    setShowMoveMenu(false);
    setFolderSearchText('');

    try {
      const results = await Promise.allSettled(ids.map((id) => setConversationFolder(id, folderId)));
      const succeededIds = new Set(ids.filter((_, index) => results[index].status === 'fulfilled'));
      const failedIds = ids.filter((id) => !succeededIds.has(id));

      setConversations((prev) => prev.map((c) =>
        succeededIds.has(c.conversation_id) ? { ...c, folder_id: folderId } : c
      ));
      // Keep the failed ones selected so the user can see what didn't move
      // and retry, instead of clearing the selection as if everything succeeded.
      setSelectedIds(new Set(failedIds));
      if (failedIds.length > 0) {
        alert(`Failed to move ${failedIds.length} conversation(s)`);
      }
    } finally {
      setMovingSelection(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  // A conversation filed under a Project lives in THAT project and nowhere
  // else: it's listed in the project's own History panel and opens inside the
  // project's shell. Mixing them in here would put one project's conversations
  // next to another's (and next to unfiled ones) with nothing but their titles
  // to tell them apart -- exactly the separation a project exists to provide.
  //
  // NOTE: `project_id` (project membership) is unrelated to `has_project`,
  // which means "this conversation generated a vibecoder website" -- see the
  // ConversationResponse model's own note. The Apps filter uses the latter and
  // is untouched by this.
  const generalConversations = conversations.filter((conversation) => !conversation.project_id);
  const projectConversationCount = conversations.length - generalConversations.length;

  const getFilteredConversations = () => {
    const base = showOnlyProjects
      ? generalConversations.filter(conversation => conversation.has_project)
      : generalConversations;

    if (!filterText.trim()) {
      return base;
    }

    const searchText = filterText.toLowerCase();
    const folderNameById = new Map(folders.map((f) => [f.folder_id, f.name.toLowerCase()]));

    return base.filter(conversation => {
      const nameMatch = conversation.name.toLowerCase().includes(searchText);
      const previewMatch = conversation.last_message_preview?.toLowerCase().includes(searchText);
      // A conversation also shows up if the FOLDER it's filed under matches -
      // typing a folder's name surfaces everything inside it, not just
      // conversations whose own title/preview happens to match too.
      const folderName = conversation.folder_id ? folderNameById.get(conversation.folder_id) : null;
      const folderMatch = folderName ? folderName.includes(searchText) : false;
      // And by its focus mode tag: "3190" or "proposal" finds the rows tagged
      // with that mode.
      const focusModeMatch = focusModeTagMatches(conversation.focus_mode, searchText);
      return nameMatch || previewMatch || folderMatch || focusModeMatch;
    });
  };

  const filteredConversations = getFilteredConversations();
  // Project conversations aren't rendered here, so they can't be selected --
  // a footer whose every action ("Delete selected", "Move to folder") applies
  // to the visible list shouldn't appear just because they exist.
  const showFooter = generalConversations.length > 0;
  const activeSearchText = filterText.trim().toLowerCase();

  const conversationsByFolder = new Map();
  folders.forEach((folder) => conversationsByFolder.set(folder.folder_id, []));
  const unfiledConversations = [];
  filteredConversations.forEach((conversation) => {
    if (conversation.folder_id && conversationsByFolder.has(conversation.folder_id)) {
      conversationsByFolder.get(conversation.folder_id).push(conversation);
    } else {
      unfiledConversations.push(conversation);
    }
  });

  // While actively searching, hide folders that neither match by name nor
  // contain any matching conversation - otherwise every folder header would
  // stay visible with an empty body, which reads as "nothing found" noise.
  const visibleFolders = activeSearchText
    ? folders.filter((folder) =>
        folder.name.toLowerCase().includes(activeSearchText) ||
        (conversationsByFolder.get(folder.folder_id) || []).length > 0
      )
    : folders;

  const batchFolderMatches = showMoveMenu ? getFolderMatches(null) : null;
  // No folders yet: no "Folders" heading, and the new-folder button sits on
  // "General conversations" instead.
  const hasFolders = folders.length > 0;
  const showFirstFolderButton = foldersLoaded && !hasFolders;

  const renderNewFolderButton = () => (
    <button
      type="button"
      className="folder-create-btn"
      onClick={(e) => {
        e.stopPropagation();
        setIsCreatingFolder(true);
      }}
      title="New folder"
      aria-label="New folder"
    >
      <FiFolderPlus size={14} />
    </button>
  );

  const renderFolderCreateForm = () => isCreatingFolder && (
    <div className="folder-create-form">
      <input
        type="text"
        value={newFolderName}
        onChange={(e) => setNewFolderName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleCreateFolder();
          if (e.key === 'Escape') handleCancelCreateFolder();
        }}
        placeholder="Folder name"
        autoFocus
        className="folder-create-input"
      />
      <div className="folder-create-buttons">
        <button
          className="save-btn"
          onClick={handleCreateFolder}
          disabled={!newFolderName.trim()}
        >
          <FiCheck />
        </button>
        <button className="cancel-btn" onClick={handleCancelCreateFolder}>
          <FiX />
        </button>
      </div>
    </div>
  );

  const renderConversationItem = (conversation) => {
    const isLoadingThis = loadingConversationId === conversation.conversation_id;
    const isAnotherLoading = loadingConversationId && !isLoadingThis;
    const isSelected = selectedIds.has(conversation.conversation_id);
    const isBeingDragged = draggedConversationId === conversation.conversation_id;
    const isEditingThis = editingId === conversation.conversation_id;
    const isMoveMenuOpen = moveMenuConversationId === conversation.conversation_id;
    const folderMatches = isMoveMenuOpen ? getFolderMatches(conversation.folder_id || null) : null;
    return (
      <div
        key={conversation.conversation_id}
        className={`conversation-item ${
          conversation.conversation_id === activeConversationId ? 'active' : ''
        } ${isLoadingThis ? 'loading' : ''} ${isAnotherLoading ? 'disabled' : ''}${
          organizeMode ? ' selectable' : ''
        }${isSelected ? ' selected' : ''}${isBeingDragged ? ' dragging' : ''}${
          isMoveMenuOpen ? ' move-menu-open' : ''
        }`}
        aria-busy={isLoadingThis}
        draggable={!organizeMode && !isEditingThis && !isLoadingThis}
        onDragStart={(e) => handleConversationDragStart(e, conversation.conversation_id)}
        onDragEnd={handleConversationDragEnd}
        onClick={organizeMode ? () => toggleSelection(conversation.conversation_id) : undefined}
      >
        {isEditingThis ? (
          <div className="conversation-edit-form">
            <input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEdit(conversation.conversation_id);
                if (e.key === 'Escape') handleCancelEdit();
              }}
              autoFocus
              className="conversation-edit-input"
            />
            <div className="conversation-edit-buttons">
              <button
                className="save-btn"
                onClick={() => handleSaveEdit(conversation.conversation_id)}
                disabled={!editingName.trim()}
              >
                <FiCheck />
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancelEdit}
              >
                <FiX />
              </button>
            </div>
          </div>
        ) : (
          <>
            {organizeMode && (
              <input
                type="checkbox"
                className="conversation-select-checkbox"
                checked={isSelected}
                onChange={() => toggleSelection(conversation.conversation_id)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select ${conversation.name}`}
              />
            )}
            <div
              className="conversation-content"
              onClick={organizeMode ? undefined : () => handleLoadConversation(conversation)}
            >
              <div className="conversation-name">
                {isLoadingThis && <span className="conversation-loading-spinner" aria-hidden="true" />}
                {conversation.has_project && (
                  <LuCodeXml
                    size={16}
                    className="conversation-project-icon"
                    title="Has a project"
                    aria-label="Has a project"
                  />
                )}
                {/* A read-only copy of a group chat they're no longer in. */}
                {conversation.left_group && (
                  <FiUsers
                    size={16}
                    className="conversation-project-icon"
                    title={`${leftGroupText(conversation.left_group)} Read only.`}
                    aria-label="Copy of a group chat, read only"
                  />
                )}
                <FocusModeTag focusMode={conversation.focus_mode} />
                <span className="conversation-name-text">{conversation.name}</span>
                <span className="conversation-time">
                  {formatDate(conversation.updated_at)}
                </span>
              </div>
              {conversation.last_message_preview && (
                <div className="conversation-preview">
                  {conversation.last_message_preview}
                </div>
              )}
            </div>
            {!organizeMode && (
              <div className={`conversation-actions${isMoveMenuOpen ? ' menu-open' : ''}`}>
                {folders.length > 0 && (
                  <div className="conversation-move-wrapper">
                    <button
                      type="button"
                      className="conversation-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMoveMenuFor(conversation.conversation_id);
                      }}
                      title="Move to folder"
                      aria-label="Move to folder"
                    >
                      <FiCornerUpRight />
                    </button>
                    {isMoveMenuOpen && (
                      <div className="move-to-folder-menu conversation-move-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          className="move-to-folder-search"
                          placeholder="Search folders"
                          value={folderSearchText}
                          onChange={(e) => setFolderSearchText(e.target.value)}
                          autoFocus
                        />
                        {folderMatches.map((folder) => (
                          <button
                            key={folder.folder_id}
                            type="button"
                            role="menuitem"
                            className="move-to-folder-menu-item"
                            onClick={() => handleMoveConversationTo(conversation.conversation_id, folder.folder_id)}
                          >
                            <FolderIcon folder={folder} size={13} /> {folder.name}
                          </button>
                        ))}
                        {folderMatches.length === 0 && (
                          <div className="move-to-folder-no-matches">No folders match</div>
                        )}
                        {conversation.folder_id && (
                          <button
                            type="button"
                            role="menuitem"
                            className="move-to-folder-menu-item"
                            onClick={() => handleMoveConversationTo(conversation.conversation_id, null)}
                          >
                            General conversations
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <button
                  className="conversation-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartEdit(conversation);
                  }}
                  title="Rename"
                >
                  <FiEdit2 />
                </button>
                <button
                  className="conversation-action-btn delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteModal([conversation.conversation_id]);
                  }}
                  title="Delete"
                >
                  <FiTrash2 />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // A "Project-<name>" folder shows the project's own folders inside it: a
  // conversation saved out of a project remembers the folder it was filed in
  // there (left_project_folder_id/_name), and a restore puts it back in it.
  // Display-only -- moving a conversation still means moving it into or out
  // of the folder itself.
  const savedSubfolderOf = (conversation) => (
    conversation.left_project_folder_id && conversation.left_project_folder_name
      ? conversation.left_project_folder_id
      : null
  );

  const renderSavedProjectSubfolders = (folder, members, openForSearch) => {
    const groups = new Map();
    members.forEach((conversation) => {
      const subfolderId = savedSubfolderOf(conversation);
      if (!subfolderId) return;
      if (!groups.has(subfolderId)) {
        groups.set(subfolderId, { name: conversation.left_project_folder_name, items: [] });
      }
      groups.get(subfolderId).items.push(conversation);
    });
    return [...groups.entries()]
      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
      .map(([subfolderId, group]) => {
        // Shares the folders' own open/closed sets, under a key no folder id can take.
        const key = `${folder.folder_id}/${subfolderId}`;
        const isCollapsed = openForSearch
          ? searchClosedFolderIds.has(key)
          : !expandedFolderIds.has(key);
        return (
          <div key={key} className="folder-group folder-subgroup">
            <div className="folder-header" onClick={() => toggleFolderCollapsed(key, openForSearch)}>
              <button
                type="button"
                className="folder-header-main"
                aria-expanded={!isCollapsed}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolderCollapsed(key, openForSearch);
                }}
              >
                {isCollapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />}
                <FiFolder size={14} className="folder-icon" />
                <span className="folder-name">{group.name}</span>
                <span className="folder-count kai-count-badge">{group.items.length}</span>
              </button>
            </div>
            {!isCollapsed && (
              <div className="folder-conversations">
                {group.items.map(renderConversationItem)}
              </div>
            )}
          </div>
        );
      });
  };

  const renderFolderGroup = (folder) => {
    const members = conversationsByFolder.get(folder.folder_id) || [];
    // While actively searching, a folder containing a match opens to show it,
    // unless the user has closed it during this search. Otherwise closed by
    // default -- only a folder in expandedFolderIds (this session only, see
    // its own comment) is open.
    const openForSearch = Boolean(activeSearchText && members.length > 0);
    const isCollapsed = openForSearch
      ? searchClosedFolderIds.has(folder.folder_id)
      : !expandedFolderIds.has(folder.folder_id);
    const isDragTarget = dragOverTarget === folder.folder_id;
    const isEditingThis = editingFolderId === folder.folder_id;
    return (
      <div key={folder.folder_id} className="folder-group">
        <div
          className={`folder-header${isDragTarget ? ' drag-over' : ''}`}
          onClick={isEditingThis ? undefined : () => toggleFolderCollapsed(folder.folder_id, openForSearch)}
          onDragOver={(e) => handleFolderDragOver(e, folder.folder_id)}
          onDragLeave={() => handleFolderDragLeave(folder.folder_id)}
          onDrop={(e) => handleFolderDrop(e, folder.folder_id)}
        >
          {isEditingThis ? (
            <div className="folder-edit-form" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={editingFolderName}
                onChange={(e) => setEditingFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveFolderEdit(folder.folder_id);
                  if (e.key === 'Escape') handleCancelFolderEdit();
                }}
                autoFocus
                className="folder-edit-input"
              />
              <div className="folder-edit-buttons">
                <button
                  className="save-btn"
                  onClick={() => handleSaveFolderEdit(folder.folder_id)}
                  disabled={!editingFolderName.trim()}
                >
                  <FiCheck />
                </button>
                <button className="cancel-btn" onClick={handleCancelFolderEdit}>
                  <FiX />
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* The toggle is a real <button> (sibling of the rename/delete
                  buttons) rather than a role="button" on the row itself -
                  nesting those inside a button-role container would hide them
                  from assistive tech. The row keeps its own onClick so
                  clicking anywhere still toggles for mouse users; this
                  button's handler stops propagation so it doesn't double-fire. */}
              <button
                type="button"
                className="folder-header-main"
                aria-expanded={!isCollapsed}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolderCollapsed(folder.folder_id, openForSearch);
                }}
              >
                {isCollapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />}
                <FolderIcon folder={folder} size={14} className="folder-icon" />
                <span className="folder-name">{folder.name}</span>
                <span className="folder-count kai-count-badge">{members.length}</span>
              </button>
              <div className="folder-actions">
                <button
                  type="button"
                  className="conversation-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartEditFolder(folder);
                  }}
                  title="Rename folder"
                >
                  <FiEdit2 />
                </button>
                <button
                  type="button"
                  className="conversation-action-btn delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    openFolderDeleteModal(folder.folder_id);
                  }}
                  title="Delete folder"
                >
                  <FiTrash2 />
                </button>
              </div>
            </>
          )}
        </div>
        {!isCollapsed && (
          <div className="folder-conversations">
            {folder.from_project && renderSavedProjectSubfolders(folder, members, openForSearch)}
            {(folder.from_project ? members.filter((c) => !savedSubfolderOf(c)) : members)
              .map(renderConversationItem)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="conversations-sidebar">
      <div className="conversations-header">
        <div className="conversations-header-content">
          <h3>Conversations</h3>
        </div>
        <button
          className="close-sidebar-btn"
          onClick={onClose}
          title="Close conversations panel"
          aria-label="Close conversations panel"
        >
          <FiX size={16} />
        </button>
      </div>

      <div className="conversations-filter">
        <div className="filter-input-wrapper">
          <input
            type="text"
            className="filter-input"
            placeholder="Filter conversations"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {filterText && (
            <button
              type="button"
              className="filter-clear-btn"
              onClick={() => setFilterText('')}
              title="Clear filter"
              aria-label="Clear filter"
            >
              <FiX size={14} />
            </button>
          )}
        </div>
        <div className="apps-filter-toggle">
          <span className="apps-filter-label">Apps</span>
          <button
            type="button"
            aria-pressed={showOnlyProjects}
            aria-label="Show only conversations with a project"
            title="Show only conversations with a project"
            className={`apps-filter-btn${showOnlyProjects ? ' active' : ''}`}
            onClick={() => setShowOnlyProjects(v => !v)}
          >
            <LuCodeXml size={14} />
          </button>
        </div>
      </div>

      <div className={`conversations-list${organizeMode ? ' has-footer' : ''}`}>

        {hasFolders && (
          <div
            className={`folders-toolbar${foldersSectionCollapsed ? ' collapsed' : ''}`}
            onClick={toggleFoldersSection}
          >
            {/* Same pattern as .folder-header: the toggle is a real <button>
                sibling of the "new folder" button rather than a button role on
                the bar itself, so both stay reachable by assistive tech. The
                bar keeps onClick so clicking anywhere still toggles. */}
            <button
              type="button"
              className="folders-toolbar-label"
              aria-expanded={!foldersSectionCollapsed}
              onClick={(e) => {
                e.stopPropagation();
                toggleFoldersSection();
              }}
            >
              Folders
              {foldersSectionCollapsed && (
                <span className="folders-toolbar-count">{folders.length}</span>
              )}
            </button>
            {renderNewFolderButton()}
          </div>
        )}
        {renderFolderCreateForm()}
        {loading ? (
          <div className="conversations-loading">Loading conversations...</div>
        ) : (
          <>
            {!foldersSectionCollapsed && visibleFolders.map(renderFolderGroup)}
            {/* Same pattern as .folders-toolbar/.folder-create-btn above --
                the organize-mode entry point used to live as a full-width
                button pinned in the footer; it's here now, right beside the
                section it actually acts on. Still a drop target for
                unfiling a conversation dragged out of a folder, same as
                before. Shown whenever there's anything to organize
                (showFooter), and before the first folder, since that's where
                the new-folder button sits until a "Folders" heading exists. */}
            {(showFooter || showFirstFolderButton) && (
              <div
                className={`unfiled-section-label${dragOverTarget === 'unfiled' ? ' drag-over' : ''}`}
                onDragOver={(e) => handleFolderDragOver(e, 'unfiled')}
                onDragLeave={() => handleFolderDragLeave('unfiled')}
                onDrop={(e) => handleFolderDrop(e, 'unfiled')}
              >
                <span className="unfiled-section-label-text">General conversations</span>
                {showFirstFolderButton && renderNewFolderButton()}
                {showFooter && (
                  <button
                    type="button"
                    className="unfiled-organize-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      enterOrganizeMode();
                    }}
                    disabled={editingId !== null}
                    title="Organize conversations"
                    aria-label="Organize conversations"
                  >
                    <IoFileTrayFullOutline size={14} />
                  </button>
                )}
              </div>
            )}
            {unfiledConversations.map(renderConversationItem)}
            {filteredConversations.length === 0 && (
              <div className="conversations-empty">
                {(filterText || showOnlyProjects) ? (
                  <>
                    <p>No conversations match your filter</p>
                    <p className="conversations-empty-hint">
                      {showOnlyProjects && !filterText
                        ? 'No conversations have a project yet'
                        : 'Try a different search term'}
                    </p>
                  </>
                ) : projectConversationCount > 0 ? (
                  // Not "no conversations yet" -- they have some, they're just
                  // all filed under projects. Saying otherwise reads as data loss.
                  <>
                    <p>No conversations here yet</p>
                    <p className="conversations-empty-hint">
                      {projectConversationCount === 1
                        ? 'Your one conversation is filed under a project'
                        : `All ${projectConversationCount} of your conversations are filed under projects`}
                      {' — open the project from your profile menu to find '}
                      {projectConversationCount === 1 ? 'it' : 'them'}.
                    </p>
                  </>
                ) : (
                  <>
                    <p>No conversations yet</p>
                    <p className="conversations-empty-hint">Start typing a message to create your first conversation</p>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {organizeMode && (
        <div className="conversations-footer">
          <div className="conversations-footer-actions">
              <button
                type="button"
                className="conversations-delete-selected-btn"
                onClick={handleOpenBatchDeleteModal}
                disabled={selectedIds.size === 0 || deleting}
              >
                Delete selected ({selectedIds.size})
              </button>
              {folders.length > 0 && (
                <div className="move-to-folder-wrapper">
                  <button
                    type="button"
                    className="conversations-move-selected-btn"
                    onClick={() => {
                      setShowMoveMenu((v) => !v);
                      setFolderSearchText('');
                    }}
                    disabled={selectedIds.size === 0 || deleting || movingSelection}
                  >
                    Move to folder
                  </button>
                  {showMoveMenu && (
                    <div className="move-to-folder-menu" role="menu">
                      <input
                        type="text"
                        className="move-to-folder-search"
                        placeholder="Search folders"
                        value={folderSearchText}
                        onChange={(e) => setFolderSearchText(e.target.value)}
                        autoFocus
                      />
                      {batchFolderMatches.map((folder) => (
                        <button
                          key={folder.folder_id}
                          type="button"
                          role="menuitem"
                          className="move-to-folder-menu-item"
                          onClick={() => handleMoveSelectedToFolder(folder.folder_id)}
                        >
                          <FolderIcon folder={folder} size={13} />
                          <span className="move-to-folder-menu-item-label">{folder.name}</span>
                        </button>
                      ))}
                      {batchFolderMatches.length === 0 && (
                        <div className="move-to-folder-no-matches">No folders match</div>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        className="move-to-folder-menu-item"
                        onClick={() => handleMoveSelectedToFolder(null)}
                      >
                        General conversations
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                className="conversations-cancel-organize-btn"
                onClick={cancelOrganizeMode}
                disabled={deleting}
              >
                Cancel
              </button>
          </div>
        </div>
      )}

      {/* Same bottom-pinned treatment as the project sidebar's own "New
          conversation" button (see .project-panel-pane-actions-footer) --
          stays visible no matter how far the folders/conversations list
          scrolls, instead of sitting above it and scrolling out of view.
          Hidden during organize mode so it doesn't sit underneath the
          delete/move/cancel footer, which is bottom-pinned the same way. */}
      {!organizeMode && (
        <div className="conversations-actions conversations-actions-footer">
          <button
            className="new-conversation-btn"
            // Reset in place (App.js's handleNewConversation) rather than
            // navigating to "/", so the sidebar stays open on desktop.
            onClick={() => onNewConversation()}
          >
            <FiPlus /> New Conversation
          </button>
        </div>
      )}

      {deleteModalIds && (
        <div className="conversations-modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div
            className="conversations-modal"
            role="dialog"
            aria-labelledby="conversations-delete-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="conversations-delete-title">
              {deleteModalIds.length === 1
                ? 'Delete conversation?'
                : `Delete ${deleteModalIds.length} conversations?`}
            </h4>
            <p>
              {deleteModalIds.length === 1
                ? 'This conversation and all its messages will be permanently deleted. This cannot be undone.'
                : 'These conversations and all their messages will be permanently deleted. This cannot be undone.'}
            </p>
            {deleteModalIds.some(
              (id) => conversations.find((c) => c.conversation_id === id)?.has_project
            ) && (
              <p className="conversations-modal-warning">
                {deleteModalIds.length === 1
                  ? 'This conversation is connected to a vibe-coded app. Deleting it will also permanently delete that app and its files, and anyone with the link will see it as unavailable.'
                  : 'One or more of these conversations are connected to a vibe-coded app. Deleting them will also permanently delete those apps and their files, and anyone with the link will see them as unavailable.'}
              </p>
            )}
            <div className="conversations-modal-actions">
              <button
                type="button"
                className="conversations-modal-cancel"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="conversations-modal-confirm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {folderDeleteId && (
        <div className="conversations-modal-overlay" onClick={closeFolderDeleteModal} role="presentation">
          <div
            className="conversations-modal"
            role="dialog"
            aria-labelledby="folder-delete-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="folder-delete-title">Delete folder?</h4>
            <p>
              The folder will be deleted. Conversations inside it are not
              deleted — they move back to Unfiled.
            </p>
            <div className="conversations-modal-actions">
              <button
                type="button"
                className="conversations-modal-cancel"
                onClick={closeFolderDeleteModal}
                disabled={deletingFolder}
              >
                Cancel
              </button>
              <button
                type="button"
                className="conversations-modal-confirm"
                onClick={confirmDeleteFolder}
                disabled={deletingFolder}
              >
                {deletingFolder ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ConversationsSidebar;
